import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "wouter";
import { apiFetch, transcribeVoice, synthesizeSpeech } from "../lib/api.js";

function GroundSetScreen({ navigate }) {
  return (
    <div className="page ground-set-screen">
      <div className="kicker" style={{ marginBottom: 32 }}>
        <span className="the-line" />
        YOUR CORNER
      </div>
      <h1 className="ground-set-heading">Your ground is set.</h1>
      <p className="ground-set-copy">
        Vision sketch, quarterly goals, your floor, and your first Big 3 - it's all there now.
        Home shows you everything you built here.
      </p>
      <p className="ground-set-sub">
        Monday is review day. That's when you score last week, set the next Big 3, and check the
        floor. Your Corner runs it with you.
      </p>
      <div style={{ marginTop: 40 }}>
        <button className="btn-primary" onClick={() => navigate("/")}>
          Open Home
        </button>
      </div>
    </div>
  );
}

export default function CoachSession() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [starter, setStarter] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [ended, setEnded] = useState(false);
  const logRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [playback, setPlayback] = useState({});
  const activeAudioRef = useRef(null);

  useEffect(() => {
    apiFetch(`/sessions/${id}`).then((s) => {
      setSession(s);
      setMessages(s.messages);
      setEnded(s.ended);
      const stored = sessionStorage.getItem(`coach-starter-${id}`);
      if (stored) setStarter(stored);
    }).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages]);

  async function sendMessage(content) {
    if (!content.trim() || sending || ended) return;
    setError(null);
    setSending(true);
    setDraft("");
    sessionStorage.removeItem(`coach-starter-${id}`);
    setStarter(null);
    setMessages((prev) => [...prev, { role: "user", content }]);
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`/api/sessions/${id}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });

      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Your corner could not respond.");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.type === "text") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + payload.text };
              return next;
            });
          } else if (payload.type === "error") {
            setError(payload.message);
          }
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  async function endSession() {
    setSending(true);
    try {
      await apiFetch(`/sessions/${id}/end`, { method: "POST" });
      setEnded(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  async function startRecording() {
    setVoiceError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError("Voice input is not supported in this browser. You can still type.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (blob.size === 0) {
          setVoiceError("No audio was recorded. Try again.");
          return;
        }
        setTranscribing(true);
        try {
          const text = await transcribeVoice(blob);
          setDraft((prev) => (prev ? `${prev} ${text}` : text));
        } catch (e) {
          setVoiceError(`${e.message} You can still type your message.`);
        } finally {
          setTranscribing(false);
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (e) {
      const denied = e.name === "NotAllowedError" || e.name === "PermissionDeniedError";
      setVoiceError(
        denied
          ? "Microphone access was denied. You can still type your message."
          : "Could not access the microphone. You can still type your message."
      );
    }
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setRecording(false);
  }

  async function togglePlayback(index, text) {
    const current = playback[index] || { status: "idle" };

    if (current.status === "playing") {
      activeAudioRef.current?.pause();
      setPlayback((p) => ({ ...p, [index]: { ...current, status: "paused" } }));
      return;
    }

    if (current.status === "paused" && current.url) {
      activeAudioRef.current?.play();
      setPlayback((p) => ({ ...p, [index]: { ...current, status: "playing" } }));
      return;
    }

    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current = null;
    }

    setPlayback((p) => ({ ...p, [index]: { status: "loading" } }));
    try {
      const url = await synthesizeSpeech(text);
      const audio = new Audio(url);
      activeAudioRef.current = audio;
      audio.onended = () => setPlayback((p) => ({ ...p, [index]: { status: "idle", url } }));
      await audio.play();
      setPlayback((p) => ({ ...p, [index]: { status: "playing", url } }));
    } catch (e) {
      setPlayback((p) => ({ ...p, [index]: { status: "error", error: e.message } }));
    }
  }

  function stopPlayback(index) {
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.currentTime = 0;
    }
    setPlayback((p) => ({ ...p, [index]: { status: "idle" } }));
  }

  if (!session) return null;

  if (ended && session.type === "first") {
    return <GroundSetScreen navigate={navigate} />;
  }

  return (
    <div className="page">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        YOUR CORNER
      </div>

      {messages.length === 0 && starter && (
        <div className="quickstarts">
          <button className="btn-secondary" onClick={() => sendMessage(starter)}>
            {starter.split("\n")[0]}
          </button>
        </div>
      )}

      <div className="chat-window">
        <div className="chat-log" ref={logRef}>
          {messages.length === 0 && <p className="empty-state">Send a message to begin.</p>}
          {messages.map((m, i) => {
            const pb = playback[i] || { status: "idle" };
            const canSpeak = m.role === "assistant" && m.content && !(sending && i === messages.length - 1);
            return (
              <div key={i} className={`msg ${m.role === "user" ? "user" : "coach"}`}>
                {m.role === "assistant" && <div className="coach-kicker">Your corner</div>}
                {m.content || (sending && i === messages.length - 1 ? "…" : "")}
                {canSpeak && (
                  <div className="voice-playback">
                    <button
                      type="button"
                      className="voice-btn"
                      onClick={() => togglePlayback(i, m.content)}
                      disabled={pb.status === "loading"}
                      aria-label={pb.status === "playing" ? "Pause reply" : "Play reply"}
                    >
                      {pb.status === "loading" ? "Loading…" : pb.status === "playing" ? "Pause" : "Play"}
                    </button>
                    {(pb.status === "playing" || pb.status === "paused") && (
                      <button type="button" className="voice-btn" onClick={() => stopPlayback(i)} aria-label="Stop reply">
                        Stop
                      </button>
                    )}
                    {pb.status === "error" && <span className="voice-error">{pb.error}</span>}
                  </div>
                )}
              </div>
            );
          })}
          {ended && <div className="msg notice">This session has ended.</div>}
        </div>
        <div className="chat-bar">
          <button
            type="button"
            className={`voice-mic-btn${recording ? " recording" : ""}`}
            onClick={recording ? stopRecording : startRecording}
            disabled={ended || sending || transcribing}
            aria-label={recording ? "Stop recording" : "Record a voice message"}
            title={recording ? "Stop recording" : "Record a voice message"}
          >
            {recording ? "■" : "●"}
          </button>
          <textarea
            placeholder={ended ? "Session ended" : transcribing ? "Transcribing…" : "Message your corner…"}
            value={draft}
            disabled={ended || sending || transcribing}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(draft);
              }
            }}
          />
          <button className="btn-primary" disabled={ended || sending || !draft.trim()} onClick={() => sendMessage(draft)}>
            Send
          </button>
        </div>
      </div>

      {recording && <p className="voice-status">Recording… tap stop when you're done.</p>}
      {transcribing && <p className="voice-status">Transcribing your recording…</p>}
      {voiceError && <p style={{ color: "var(--error)", marginTop: 8 }}>{voiceError}</p>}

      {error && <p style={{ color: "var(--error)", marginTop: 16 }}>{error}</p>}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="btn-secondary" onClick={() => navigate("/coach")}>Back to sessions</button>
        {!ended && (
          <button className="btn-secondary" onClick={endSession} disabled={sending}>
            End session
          </button>
        )}
      </div>
    </div>
  );
}
